/**
 * Aerial control — car rotation while airborne.
 *
 * Implements https://www.smish.dev/rocket_league/aerial_control :
 *
 *   tau           = Theta * (T * u + D * Theta^T * omega)
 *   omega(t + dt) = omega(t) + tau * dt
 *   Theta(t + dt) = axisToRotation(omega * dt) * Theta(t)
 *
 * where Theta is the orientation matrix (columns = forward, left, up), u is the
 * (roll, pitch, yaw) input vector, T is the diagonal torque matrix and D the
 * diagonal damping matrix.
 *
 * The non-obvious rule, and the one that makes aerials feel right: damping on
 * the pitch and yaw axes is scaled by (1 - |input|), so an axis held at full
 * deflection has NO damping at all. Roll damping is always on. This is why
 * holding full pitch spins up faster than you would expect from the torque
 * coefficient alone, and why the car keeps rotating when you release.
 */

import { AERIAL_DAMPING, AERIAL_TORQUE, CAR_MAX_ANGULAR_SPEED } from './constants';
import type { CarInput, CarState } from './car-state';
import {
  clamp,
  clampLengthBy,
  integrateRotation,
  mulVec,
  mulVecT,
  type Mat3,
  type Vec3,
  vec3,
} from './math';

/**
 * Angular acceleration (world frame) for the given orientation, angular
 * velocity and input. Exposed separately from the integrator so tests can
 * assert the torque model directly.
 *
 * `u` components are (roll, pitch, yaw), each clamped to [-1, 1].
 */
export const aerialTorque = (orientation: Mat3, angularVelocity: Vec3, u: Vec3): Vec3 => {
  const roll = clamp(u.x, -1, 1);
  const pitch = clamp(u.y, -1, 1);
  const yaw = clamp(u.z, -1, 1);

  // Angular velocity expressed in the car's local frame: Theta^T * omega.
  const wLocal = mulVecT(orientation, angularVelocity);

  // T*u + D*(Theta^T omega), with the input-dependent damping cutoff.
  // Local axis order is (forward, left, up) = (roll, pitch, yaw).
  const local = vec3(
    AERIAL_TORQUE.roll * roll + AERIAL_DAMPING.roll * wLocal.x,
    AERIAL_TORQUE.pitch * pitch + AERIAL_DAMPING.pitch * (1 - Math.abs(pitch)) * wLocal.y,
    AERIAL_TORQUE.yaw * yaw + AERIAL_DAMPING.yaw * (1 - Math.abs(yaw)) * wLocal.z
  );

  // Back to world space.
  return mulVec(orientation, local);
};

/**
 * Integrate the car's rotation for one airborne tick.
 * Mutates `car.angularVelocity` and `car.orientation`.
 */
export const stepAerialRotation = (car: CarState, input: CarInput, dt: number): void => {
  // Air roll modifier: holding the air-roll button maps the steer axis onto
  // roll, which is how directional air roll is played.
  const roll = input.handbrake ? input.steer : input.roll;
  const yaw = input.handbrake ? 0 : input.yaw;

  const tau = aerialTorque(car.orientation, car.angularVelocity, vec3(roll, input.pitch, yaw));

  car.angularVelocity.x += tau.x * dt;
  car.angularVelocity.y += tau.y * dt;
  car.angularVelocity.z += tau.z * dt;
  clampLengthBy(car.angularVelocity, CAR_MAX_ANGULAR_SPEED);

  integrateOrientation(car, dt);
};

/**
 * Advance the orientation matrix by the current angular velocity.
 * Uses the matrix exponential (Rodrigues), matching RLUtilities, and
 * re-orthonormalizes to stop floating-point drift from shearing the basis
 * over a long match.
 *
 * MUTATES `car.orientation` IN PLACE rather than replacing it. A caller holding
 * the matrix across a tick sees the new value — which is the same aliasing rule
 * the whole state API follows (see src/sim/state.ts). The in-place form is
 * bit-identical to the allocating one and was worth about 8 % of the tick.
 */
export const integrateOrientation = (car: CarState, dt: number): void => {
  integrateRotation(car.orientation, car.angularVelocity, dt);
};
